import type { DefaultOptionType } from "antd/es/cascader";
import type { CategoryNode } from "../../pages/inventory/inventoryV15Helpers";

export const CATEGORY_PATH_SEPARATOR = " / ";
export const RECENT_CATEGORY_LIMIT = 5;
export const RECENT_CATEGORY_STORAGE_KEY =
  "ember.admin.inventory.recent-category-ids.v1";

export type CategoryLoadState =
  "idle" | "loading" | "ready" | "empty" | "error" | "forbidden";

export type CategoryPathEntry = {
  categoryId: number;
  ids: number[];
  names: string[];
  label: string;
  isLeaf: boolean;
};

export type CategoryPathIndex = ReadonlyMap<number, CategoryPathEntry>;

export type CategoryCascaderOption = DefaultOptionType & {
  value: number;
  label: string;
  isLeaf: boolean;
  children?: CategoryCascaderOption[];
};

export function buildCategoryPathIndex(
  categories: CategoryNode[],
): Map<number, CategoryPathEntry> {
  const index = new Map<number, CategoryPathEntry>();
  const visit = (node: CategoryNode, ids: number[], names: string[]) => {
    const nextIds = [...ids, node.categoryId];
    const nextNames = [...names, node.name];
    index.set(node.categoryId, {
      categoryId: node.categoryId,
      ids: nextIds,
      names: nextNames,
      label: nextNames.join(CATEGORY_PATH_SEPARATOR),
      isLeaf: node.isLeaf,
    });
    node.children.forEach((child) => visit(child, nextIds, nextNames));
  };
  categories.forEach((category) => visit(category, [], []));
  return index;
}

export function buildCategoryCascaderOptions(
  categories: CategoryNode[],
): CategoryCascaderOption[] {
  return categories.map((category) => {
    const children = buildCategoryCascaderOptions(category.children);
    return {
      value: category.categoryId,
      label: category.name,
      isLeaf: category.isLeaf,
      ...(children.length > 0 ? { children } : {}),
    };
  });
}

export function categorySearchFilter(
  inputValue: string,
  path: CategoryCascaderOption[],
): boolean {
  const last = path[path.length - 1];
  if (!last?.isLeaf) return false;
  const query = inputValue.trim().toLocaleLowerCase("zh-CN");
  if (!query) return true;
  return path.some((option) =>
    String(option.label).toLocaleLowerCase("zh-CN").includes(query),
  );
}

export function categoryPathFromOptions(
  path: CategoryCascaderOption[],
): string {
  return path
    .map((option) => String(option.label))
    .join(CATEGORY_PATH_SEPARATOR);
}

export function categoryLabelForItem(
  categoryId: number,
  categoryName: string,
  index: CategoryPathIndex,
): string {
  return index.get(categoryId)?.label || categoryName.trim() || "品类待核对";
}

export function parseRecentCategoryIds(value: string | null): number[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is number => Number.isInteger(item) && item > 0,
    );
  } catch {
    return [];
  }
}

export function validRecentCategoryIds(
  ids: number[],
  index: CategoryPathIndex,
): number[] {
  return [...new Set(ids)]
    .filter((categoryId) => index.get(categoryId)?.isLeaf === true)
    .slice(0, RECENT_CATEGORY_LIMIT);
}

export function pushRecentCategoryId(
  ids: number[],
  categoryId: number,
  index: CategoryPathIndex,
): number[] {
  return validRecentCategoryIds(
    [categoryId, ...ids.filter((item) => item !== categoryId)],
    index,
  );
}
